import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaArrowLeft } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const toursData = [
    {
        id: 1,
        title: 'Osu Castle',
        region: 'Greater Accra',
        description: 'A 17th-century castle in Osu, Accra, formerly the seat of government.',
        image: '/assets/osucastle.jpg',
        fullContent: 'Osu Castle, also known as Fort Christiansborg, is located in Osu, Accra. Built by the Danes in the 1660s, it has changed hands between Denmark, Portugal, the Akwamu, Britain, and finally Ghana. It served as the seat of government for most of Ghana\'s history until the Jubilee House was constructed. It is a significant historical monument reflecting the colonial history and political evolution of Ghana.',
        yearBuilt: '1660s',
        images: ['/assets/osucastle1.jpg', '/assets/osucastle2.jpg', '/assets/osucastle3.jpg'],
        highlights: ['Seat of Government', 'Danish Architecture', 'Slave Trade History', 'Political Museum'],
    },
    {
        id: 2,
        title: 'Castle of Good Hope',
        region: 'Western Cape',
        description: 'The oldest existing colonial building in South Africa, a bastion fort in Cape Town.',
        image: '/assets/goodhope.jpg',
        fullContent: 'The Castle of Good Hope is a bastion fort built in the 17th century in Cape Town, South Africa. Originally located on the coastline of Table Bay, following land reclamation the fort is now located inland. It was built by the Dutch East India Company between 1666 and 1679. It served as a replenishment station for ships sailing to the Dutch East Indies. Today, it houses the Castle Military Museum and ceremonial facilities for the traditional Cape Regiments.',
        yearBuilt: '1666',
        images: ['/assets/goodhope1.jpg', '/assets/goodhope2.jpg', '/assets/goodhope3.jpg'],
        highlights: ['Military Museum', 'Dutch Architecture', 'Key Ceremony', 'Dungeons'],
    },
    {
        id: 3,
        title: 'National Museum of Ethiopia',
        region: 'Addis Ababa',
        description: 'Home to famous archaeological finds like Lucy, preserving Ethiopia\'s rich history.',
        image: '/assets/nationalethiopia.jpg',
        fullContent: 'The National Museum of Ethiopia, located in Addis Ababa, is the primary museum in Ethiopia. It houses the nation\'s artistic treasures as well as many of the most precious archaeological finds such as the fossilized remains of early hominids, the most famous being "Lucy," the partial skeleton of a specimen of Australopithecus afarensis. The museum offers a deep dive into the cradle of mankind and Ethiopian civilization.',
        yearBuilt: '1944',
        images: ['/assets/nationalethiopia2.webp', '/assets/nationalethiopia3.jpg', '/assets/nationalethiopia4.jpg'],
        highlights: ['Lucy Skeleton', 'Royal Paraphernalia', 'Ancient Art', 'Paleontological Exhibits'],
    },
];

const HistoricalTourDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const tour = toursData.find(t => t.id === parseInt(id));

    if (!tour) {
        return (
            <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
                <PageHeader />
                <Preloader />
                <main className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-4">Historical Site Not Found</h2>
                        <button onClick={() => navigate('/historical-tours')} className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                            Back to Historical Tours
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Back Button */}
                <section className="bg-white py-6 px-4 border-b">
                    <div className="container mx-auto">
                        <button onClick={() => navigate('/historical-tours')} className="flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold transition-colors duration-300">
                            <FaArrowLeft /> Back to Historical Tours
                        </button>
                    </div>
                </section>

                {/* Hero Section */}
                <section className="relative h-96 bg-cover bg-center">
                    <img src={tour.image} alt={tour.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 flex items-end justify-start">
                        <div className="p-8 text-white">
                            <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded mb-4">Built {tour.yearBuilt}</span>
                            <h1 className="text-4xl md:text-6xl font-cormorant-upright font-bold">{tour.title}</h1>
                            <p className="text-gray-200 mt-2">{tour.region}</p>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1}>
                            <article className="bg-white rounded-lg shadow-lg p-8">
                                <h2 className="text-2xl font-cormorant-upright font-bold mb-6">Historical Overview</h2>
                                <p className="text-gray-700 font-semibold leading-relaxed mb-8 text-sm">{tour.fullContent}</p>

                                {/* Photo Gallery */}
                                <div className="mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Photo Gallery</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {tour.images.map((image, index) => (
                                            <div key={index} className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                                <img
                                                    src={image}
                                                    alt={`${tour.title} ${index + 1}`}
                                                    className="w-full h-48 object-cover transform hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Highlights */}
                                <div className="border-t pt-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Key Highlights</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {tour.highlights.map((highlight, index) => (
                                            <div key={index} className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                                                <p className="text-gray-700 font-semibold text-sm">{highlight}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Historical Info */}
                                <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
                                    <h4 className="font-bold text-lg mb-4">Historical Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-blue-600 text-sm">Built</p>
                                            <p className='text-sm font-semibold'>{tour.yearBuilt}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-blue-600 text-sm">Location</p>
                                            <p className='text-sm font-semibold'>{tour.region}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Call to Action */}
                                <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded">
                                    <h4 className="font-bold text-base mb-2">Plan Your Visit</h4>
                                    <p className="mb-4 text-sm">Discover the rich history and cultural significance of this important historical site.</p>
                                    <a href='/contact-us'>
                                    <button className="px-6 py-2 capitalize text-sm bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300">
                                        get started
                                    </button>
                                    </a>
                                </div>
                            </article>
                        </ScrollReveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default HistoricalTourDetail;
